"use client";

import { useState, useEffect, useCallback } from "react";
import { authFetch } from "@/lib/authFetch";
import { createLogger } from "@/lib/logger";

const logger = createLogger("PushNotifications");

export type PushState =
  | "loading"
  | "unsupported"
  | "denied"
  | "prompt"
  | "subscribed"
  | "unsubscribed";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>("loading");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check initial state
  useEffect(() => {
    async function checkState() {
      // Feature detection
      if (
        typeof window === "undefined" ||
        !("serviceWorker" in navigator) ||
        !("PushManager" in window) ||
        !("Notification" in window)
      ) {
        setState("unsupported");
        return;
      }

      // Check permission
      const permission = Notification.permission;
      if (permission === "denied") {
        setState("denied");
        return;
      }

      if (permission === "default") {
        setState("prompt");
        return;
      }

      // Permission is "granted" — check if actively subscribed
      try {
        const registration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("SW timeout")), 5000)
          ),
        ]);
        const subscription = await registration.pushManager.getSubscription();
        setState(subscription ? "subscribed" : "unsubscribed");
      } catch {
        setState("unsubscribed");
      }
    }

    checkState();
  }, []);

  const subscribe = useCallback(async (): Promise<boolean> => {
    if (state === "unsupported") return false;
    setIsLoading(true);
    setError(null);

    try {
      // 1. Get VAPID key from backend
      const vapidRes = await authFetch("/api/push/vapid-key");
      if (!vapidRes.ok) {
        setError(`VAPID key fetch failed (${vapidRes.status})`);
        return false;
      }
      const { vapidPublicKey } = await vapidRes.json();
      if (!vapidPublicKey) {
        setError("VAPID key not configured on server");
        return false;
      }

      // 2. Request notification permission (shows browser prompt if needed)
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setState("denied");
        setError("Notification permission denied");
        return false;
      }

      // 3. Wait for SW with timeout (iOS can be slow to activate)
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Service Worker not ready (timeout 10s)")), 10000)
        ),
      ]);
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
      });

      // 4. Send subscription to backend
      const subJson = subscription.toJSON();
      const res = await authFetch("/api/push/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: {
            p256dh: subJson.keys?.p256dh,
            auth: subJson.keys?.auth,
          },
          userAgent: navigator.userAgent,
        }),
      });

      if (!res.ok) {
        setError(`Backend subscribe failed (${res.status})`);
        return false;
      }

      setState("subscribed");
      return true;
    } catch (err: any) {
      setError(err?.message || "Unknown error");
      logger.warn("Push subscribe failed", { error: err?.message });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [state]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();

        // Remove from backend
        await authFetch("/api/push/unsubscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
      }

      setState("unsubscribed");
      return true;
    } catch (err: any) {
      setError(err?.message || "Unknown error");
      logger.warn("Push unsubscribe failed", { error: err?.message });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    state,
    isLoading,
    error,
    subscribe,
    unsubscribe,
    isSupported: state !== "unsupported",
    isSubscribed: state === "subscribed",
  };
}
